/**
 * For Studios Landing Page — SEO-optimized page targeting studio owners
 * who need to hire second shooters and photography assistants
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import {
  ArrowRight, Check, Users, Zap, FileText, Star, MapPin, Camera,
  Search, Shield, Clock, ChevronDown,
} from "lucide-react";
import React from "react";

var STUDIO_FAQ = [
  {
    q: "How much does it cost for studios to use GottaSecond?",
    a: "You can post a single job for $10 with no subscription. Or subscribe to Studio Pro for $19/month for unlimited job postings, full candidate access, contract templates, and direct messaging. Cancel anytime.",
  },
  {
    q: "How do I find second shooters in my area?",
    a: "Post a job with your event details, date, location, and pay rate. GottaSecond's matching algorithm instantly surfaces the best-fit candidates in your area based on skills, gear, style, availability, and distance. No more posting and hoping.",
  },
  {
    q: "Can I see a second shooter's portfolio and reviews before hiring?",
    a: "Yes. Every candidate has a full profile with portfolio samples, gear lists, work history, and verified reviews from other studios. You can also see a 'Would Hire Again' percentage from previous clients.",
  },
  {
    q: "How do contracts and payments work?",
    a: "When you hire a second shooter, GottaSecond automatically generates a contract from your saved template. Both parties sign electronically. Payments are processed securely through Stripe — no chasing invoices.",
  },
  {
    q: "What if I only need help for one job?",
    a: "Our Pay Per Job option at $10 is designed for exactly that. Post one job, view matched candidates, message them, and hire — no subscription needed.",
  },
];

var STUDIO_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Hire Second Shooters for Your Photography Studio | GottaSecond",
  "description": "Stop posting in Facebook groups. Find vetted second shooters and photography assistants in your area with verified portfolios, reviews, and automated contracts.",
  "url": "https://gottasecond.com/for-studios",
  "isPartOf": {
    "@type": "WebSite",
    "name": "GottaSecond",
    "url": "https://gottasecond.com",
  },
  "mainEntity": {
    "@type": "Service",
    "name": "GottaSecond for Studios",
    "description": "Photography studio hiring platform for finding and managing second shooters, assistants, and videographers.",
    "provider": {
      "@type": "Organization",
      "name": "GottaSecond",
      "url": "https://gottasecond.com",
    },
    "serviceType": "Photography staffing platform",
    "areaServed": "United States",
  },
};

var FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": STUDIO_FAQ.map(function(item) {
    return {
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a },
    };
  }),
};

function FAQItem({ question, answer }) {
  var [open, setOpen] = React.useState(false);
  return (
    <div className="py-5" data-testid="studio-faq-item">
      <button
        onClick={function() { setOpen(!open); }}
        className="w-full flex items-start justify-between text-left gap-4 group"
        aria-expanded={open}
      >
        <h3 className="text-base font-semibold text-white group-hover:text-white/90 transition-colors pr-4">{question}</h3>
        <ChevronDown className={"w-5 h-5 text-white/40 shrink-0 mt-0.5 transition-transform duration-200 " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <p className="mt-3 text-white/50 text-sm leading-relaxed pr-12">{answer}</p>
      )}
    </div>
  );
}

export default function ForStudiosPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <SEO
        title="Hire Second Shooters for Your Photography Studio"
        description="Stop posting in Facebook groups. GottaSecond helps studios find vetted second shooters and photography assistants with verified portfolios, reviews, and automated contracts. Post a job for $10 or subscribe for unlimited access."
        path="/for-studios"
        jsonLd={[STUDIO_JSONLD, FAQ_JSONLD]}
      />

      <NavBar>
        <Link to="/for-second-shooters" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-for-seconds">
          For Second Shooters
        </Link>
        <Link to="/pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider" data-testid="nav-pricing">
          Pricing
        </Link>
        <Link to="/login" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          Login
        </Link>
        <Link to="/register?role=studio">
          <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-6 py-2 uppercase tracking-wide text-sm">
            Get Started
          </Button>
        </Link>
      </NavBar>

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1664806223123-c3d01da422da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHx3ZWRkaW5nJTIwcGhvdG9ncmFwaGVyJTIwd2l0aCUyMGNhbWVyYSUyMHNob290aW5nJTIwY291cGxlfGVufDB8fHx8MTc3NTc1MDg3MHww&ixlib=rb-4.1.0&q=85"
            alt="Photographer capturing a wedding couple at sunset"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/30" />
        </div>

        <div className="relative z-10 max-w-[1000px] mx-auto px-6 md:px-12">
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#3498DB] font-mono mb-4">For Studios</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6" data-testid="studio-hero-headline">
            Stop Scrambling for Help
            <br />
            <span className="text-white/80">Before Every Single Shoot</span>
          </h1>
          <p className="text-base md:text-lg text-white/60 leading-relaxed max-w-2xl mb-8">
            You shouldn't have to post in Facebook groups, scroll through DMs, and vet strangers every time you need a second shooter. GottaSecond gives you a searchable pool of verified photographers — with portfolios, reviews, and automated contracts.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register?role=studio">
              <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8 py-4 uppercase tracking-wide text-sm" data-testid="studio-hero-cta">
                Start Hiring <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-sm font-bold px-8 py-4 uppercase tracking-wide text-sm" data-testid="studio-hero-pricing">
                See Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-20 md:py-24 border-t border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-12 text-center">
            Sound Familiar?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-6" data-testid="studio-pain-1">
              <Search className="w-8 h-8 text-white/30 mb-4" />
              <h3 className="font-semibold mb-2">The Facebook Scramble</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Every new job means a fresh post in Facebook groups, hoping the right person sees it. You're starting from scratch every single time.
              </p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-6" data-testid="studio-pain-2">
              <Shield className="w-8 h-8 text-white/30 mb-4" />
              <h3 className="font-semibold mb-2">No Way to Vet Anyone</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                A DM and an Instagram link isn't enough. You need to see real reviews, gear lists, and work samples before trusting someone with your clients.
              </p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-6" data-testid="studio-pain-3">
              <FileText className="w-8 h-8 text-white/30 mb-4" />
              <h3 className="font-semibold mb-2">Contract & Payment Chaos</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Sending contracts manually, chasing W9s at year-end, and hoping everyone remembers the terms. It's a mess that shouldn't exist.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Features */}
      <section className="py-20 md:py-24 border-t border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.3em] text-[#3498DB] font-mono">The GottaSecond Way</span>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-3">
              Everything You Need to Hire with Confidence
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-[#3498DB]/10 rounded flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5 text-[#3498DB]" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Smart Matching</h3>
                <p className="text-white/50 text-sm">Post your job and instantly see candidates ranked by fit — based on skills, gear, style, distance, and availability.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-[#3498DB]/10 rounded flex items-center justify-center shrink-0">
                <Camera className="w-5 h-5 text-[#3498DB]" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Full Profiles</h3>
                <p className="text-white/50 text-sm">See portfolios, gear lists, work history, and verified reviews before you hire. No more guessing.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-[#3498DB]/10 rounded flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[#3498DB]" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Automated Contracts</h3>
                <p className="text-white/50 text-sm">Save your contract template once. Every new hire gets a pre-filled contract with e-signatures built in.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-[#3498DB]/10 rounded flex items-center justify-center shrink-0">
                <Star className="w-5 h-5 text-[#3498DB]" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Verified Reviews</h3>
                <p className="text-white/50 text-sm">Every second shooter has a rating, "Would Hire Again" percentage, and detailed feedback from real studios.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-[#3498DB]/10 rounded flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-[#3498DB]" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Location-Based Search</h3>
                <p className="text-white/50 text-sm">Set your search radius and find talent near your event. Works for local weddings and destination shoots.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-white/[0.02] rounded-lg border border-white/5">
              <div className="w-10 h-10 bg-[#3498DB]/10 rounded flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-[#3498DB]" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Build Your Bench</h3>
                <p className="text-white/50 text-sm">Save favorites and build a go-to list of reliable second shooters you've worked with before.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Summary */}
      <section className="py-20 md:py-24 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">
            Simple Pricing. No Surprises.
          </h2>
          <p className="text-white/50 mb-10">Pay only when you need help. No long-term commitments.</p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8" data-testid="studio-price-perjob">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Try It Out</p>
              <p className="text-4xl font-bold mb-1">$10</p>
              <p className="text-white/50 text-sm mb-4">per job</p>
              <ul className="space-y-2 text-white/60 text-sm text-left">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3498DB] shrink-0" /> View matched candidates</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3498DB] shrink-0" /> Direct messaging</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3498DB] shrink-0" /> No subscription required</li>
              </ul>
            </div>
            <div className="bg-[#0a0a0a] border border-[#3498DB]/30 rounded-lg p-8 relative" data-testid="studio-price-pro">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#3498DB] text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Best Value</span>
              <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Studio Pro</p>
              <p className="text-4xl font-bold mb-1">$19</p>
              <p className="text-white/50 text-sm mb-4">per month</p>
              <ul className="space-y-2 text-white/60 text-sm text-left">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3498DB] shrink-0" /> Unlimited job postings</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3498DB] shrink-0" /> Full candidate access</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3498DB] shrink-0" /> Contract templates & e-sign</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3498DB] shrink-0" /> Cancel anytime</li>
              </ul>
            </div>
          </div>

          <Link to="/register?role=studio">
            <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-8 py-4 uppercase tracking-wide text-sm" data-testid="studio-pricing-cta">
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 border-t border-white/5" data-testid="studio-faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4 tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="text-white/50 text-center mb-10 text-sm">
            Common questions from studios about hiring on GottaSecond
          </p>
          <div className="space-y-0 divide-y divide-white/8">
            {STUDIO_FAQ.map(function(item, idx) {
              return <FAQItem key={idx} question={item.q} answer={item.a} />;
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-white/[0.03] border-t border-white/5">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Your Next Second Shooter Is Already Here
          </h2>
          <p className="text-white/50 text-base mb-8">
            Stop scrambling before every shoot. Post your first job in under a minute.
          </p>
          <Link to="/register?role=studio">
            <Button className="bg-white text-black hover:bg-gray-200 rounded-sm font-bold px-10 py-4 uppercase tracking-wide text-sm" data-testid="studio-final-cta">
              Register Your Studio <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
